import { ReactNode } from 'react'

/**
 * Элемент статистики с label и value
 * @example
 * { label: 'Суточный лимит', value: '200,000 RUB' }
 */
export interface StatisticItem {
  label: string
  value: ReactNode
}

/**
 * Props для компонента StatisticsBanner
 * @property items - одномерный массив всех элементов статистики
 * @property itemsPerRow - количество элементов в одном ряду (по умолчанию 3)
 * @property titleAction - дополнительный элемент рядом с заголовком (например, кнопка)
 * @example
 * // 6 элементов, 3 в ряду = 2 ряда (3, 3)
 * <StatisticsBanner items={[...6 items]} itemsPerRow={3} />
 *
 * // 6 элементов, 4 в ряду = 2 ряда (4, 2)
 * <StatisticsBanner items={[...6 items]} itemsPerRow={4} />
 *
 * // 6 элементов, 2 в ряду = 3 ряда (2, 2, 2)
 * <StatisticsBanner items={[...6 items]} itemsPerRow={2} />
 *
 * // С кнопкой в заголовке
 * <StatisticsBanner title="Статистика" titleAction={<button>Входящие</button>} items={[...]} />
 */
export interface StatisticsBannerProps {
  title?: string
  titleAction?: ReactNode // дополнительный элемент рядом с заголовком
  mobileTitleAction?: ReactNode // дополнительный элемент для мобильной версии напротив заголовка
  items: StatisticItem[]
  itemsPerRow?: number // количество элементов в одном ряду (по умолчанию 3)
}

export const StatisticsBanner = ({
  title,
  titleAction,
  mobileTitleAction,
  items,
  itemsPerRow = 3,
}: StatisticsBannerProps) => {
  const columnClasses =
    'flex-1 flex flex-col gap-2 py-4 border-b border-b-card-border dark:border-b-card-border-dark md:py-0 md:pb-2 md:border-b-0 md:border-r md:last:border-0 border-r-card-border dark:border-r-card-border-dark md:pl-6 first:pl-0 pr-6 last:pr-0'
  const labelClasses = 'text-sm text-text-secondary'
  const dataClasses = 'font-klein font-medium'

  // Разбиваем массив на ряды по itemsPerRow
  const rows = []
  for (let i = 0; i < items.length; i += itemsPerRow) {
    rows.push(items.slice(i, i + itemsPerRow))
  }

  return (
    <div className="flex flex-col bg-light-purple dark:bg-dark-purple p-6 rounded-2xl">
      {title && (
        <div className="flex flex-col md:flex-row md:items-center justify-between md:justify-start mb-4 md:mb-6 gap-2 md:gap-[10px]">
          <div className="flex items-center gap-2 md:gap-0 justify-between">
            <div className="font-medium leading-[24px]">{title}</div>
            {mobileTitleAction && <div className="md:hidden">{mobileTitleAction}</div>}
          </div>
          {titleAction && <div className="md:ml-0">{titleAction}</div>}
        </div>
      )}

      {/* Рендерим каждый ряд */}
      {rows.map((row, rowIdx) => (
        <div
          key={rowIdx}
          className={`flex md:flex-row flex-col ${
            rowIdx === 0 ? 'md:pb-4' : 'md:pt-4'
          } ${rowIdx !== rows.length - 1 ? 'border-b-card-border md:border-b dark:border-b-card-border-dark' : ''}`}
        >
          {row.map((item, itemIdx) => (
            <div key={itemIdx} className={columnClasses}>
              <span className={labelClasses}>{item.label}</span>
              <span className={dataClasses}>{item.value}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
